import { useEffect, useState, useCallback } from 'react';
import type { Heading } from '@/types/blog';

interface UseActiveHeadingOptions {
  /** Header offset in pixels for scroll position calculation */
  headerOffset?: number;
  /** IntersectionObserver rootMargin */
  rootMargin?: string;
  /** IntersectionObserver threshold array */
  threshold?: number[];
}

/**
 * Hook to track the active heading using IntersectionObserver.
 *
 * Features:
 * - Uses IntersectionObserver for accurate active heading detection
 * - Automatically cleans up observers on unmount
 * - Simple and performant
 *
 * @param headings - Array of heading data (id, text, level)
 * @param options - Configuration options
 * @returns The ID of the currently active heading
 *
 * @example
 * ```tsx
 * const activeId = useActiveHeading(headings, {
 *   headerOffset: 96,
 * });
 * ```
 */
export function useActiveHeading(
  headings: Heading[],
  options: UseActiveHeadingOptions = {}
): string {
  const {
    headerOffset = 150,
    rootMargin,
    threshold = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
  } = options;

  // Calculate rootMargin dynamically from headerOffset if not provided
  // Top margin accounts for sticky header, bottom margin allows headings to activate before they reach the top
  const calculatedRootMargin = rootMargin || `-${headerOffset + 10}px 0% -60% 0%`;

  // Initialize with first heading if available (for immediate TOC highlighting on load)
  const [activeId, setActiveId] = useState<string>(headings.length > 0 ? headings[0].id : '');

  // Helper function to find the most visible heading from IntersectionObserver entries
  const findMostVisibleHeading = useCallback(
    (entries: IntersectionObserverEntry[]): string | null => {
      const visibleEntries = entries.filter((entry) => entry.isIntersecting);
      if (visibleEntries.length === 0) return null;

      const mostVisible = visibleEntries.reduce((prev, current) =>
        current.intersectionRatio > prev.intersectionRatio ? current : prev
      );

      // Type guard to ensure target is HTMLElement with id
      if (mostVisible.target instanceof HTMLElement && mostVisible.target.id) {
        return mostVisible.target.id;
      }

      return null;
    },
    []
  );

  useEffect(() => {
    // Don't set up observer if no headings
    if (headings.length === 0) {
      setActiveId('');
      return;
    }

    let cleanup: (() => void) | null = null;
    let scrollTimeout: NodeJS.Timeout | null = null;
    let handleScroll: (() => void) | null = null;
    let observerTimeout: NodeJS.Timeout | null = null;
    let pendingActiveId: string | null = null;

    // Use requestAnimationFrame to ensure DOM is ready before setting up observer
    // This fixes race conditions where headings are extracted before DOM elements exist
    requestAnimationFrame(() => {
      const elements: HTMLElement[] = [];

      // Find all heading elements after DOM is ready
      headings.forEach((heading) => {
        const element = document.getElementById(heading.id);
        if (element) {
          elements.push(element);
        }
      });

      // Don't set up observer if no elements found
      if (elements.length === 0) {
        setActiveId('');
        return;
      }

      // Create debounced update function to prevent rapid active heading changes
      // Use outer scope variables to share state between observer and scroll handler
      const updateActiveId = (newId: string | null) => {
        if (!newId) return;

        // Only update if heading actually changed
        if (newId === pendingActiveId) return;

        pendingActiveId = newId;

        // Debounce active heading updates to prevent jumpy TOC scrolling
        if (observerTimeout) {
          clearTimeout(observerTimeout);
        }
        observerTimeout = setTimeout(() => {
          if (pendingActiveId) {
            setActiveId(pendingActiveId);
            pendingActiveId = null;
          }
          observerTimeout = null;
        }, 150); // 150ms debounce for smoother updates
      };

      const observer = new IntersectionObserver(
        (entries) => {
          const newActiveId = findMostVisibleHeading(entries);
          if (newActiveId) {
            updateActiveId(newActiveId);
          } else {
            // If no headings are visible, find the last heading before scroll position
            // This ensures we always have an active heading when scrolling
            const scrollPosition = window.scrollY + headerOffset;
            let lastHeadingBeforeScroll = '';
            for (let i = headings.length - 1; i >= 0; i--) {
              const element = elements.find((el) => el.id === headings[i].id);
              if (element && element.offsetTop <= scrollPosition) {
                lastHeadingBeforeScroll = headings[i].id;
                break;
              }
            }
            // Only update if we found a heading (prevents clearing active state at top of page)
            if (lastHeadingBeforeScroll) {
              updateActiveId(lastHeadingBeforeScroll);
            }
          }
        },
        {
          rootMargin: calculatedRootMargin,
          threshold,
        }
      );

      // Observe all headings
      elements.forEach((element) => {
        observer.observe(element);
      });

      // Throttled scroll handler as backup for smooth updates
      // Increased throttle to reduce jumpiness
      handleScroll = () => {
        if (scrollTimeout) return;

        scrollTimeout = setTimeout(() => {
          scrollTimeout = null;

          // Find the heading closest to the top of the viewport (accounting for header)
          let closestHeading = '';
          let closestDistance = Infinity;

          for (let i = 0; i < headings.length; i++) {
            const element = elements.find((el) => el.id === headings[i].id);
            if (element) {
              const rect = element.getBoundingClientRect();
              const distance = Math.abs(rect.top - headerOffset);

              // Prefer headings that are at or above the header offset
              // Increased threshold to reduce rapid switching
              if (rect.top <= headerOffset + 50 && distance < closestDistance) {
                closestDistance = distance;
                closestHeading = headings[i].id;
              }
            }
          }

          // Only update if heading actually changed (prevents unnecessary updates)
          if (closestHeading) {
            updateActiveId(closestHeading);
          }
        }, 100); // Increased throttle to reduce jumpiness
      };

      // Add scroll listener for smoother tracking
      window.addEventListener('scroll', handleScroll, { passive: true });

      // Initial check - find first visible heading
      // If at the top of the page, default to first heading immediately (no debounce)
      const scrollPosition = window.scrollY + headerOffset;
      if (window.scrollY <= 50) {
        // At or near top of page - set first heading immediately
        if (headings.length > 0 && elements.length > 0) {
          setActiveId(headings[0].id);
          pendingActiveId = headings[0].id;
        }
      } else {
        // Scrolled down - find the appropriate heading
        for (let i = headings.length - 1; i >= 0; i--) {
          const element = elements.find((el) => el.id === headings[i].id);
          if (element && element.offsetTop <= scrollPosition) {
            updateActiveId(headings[i].id);
            break;
          }
        }
      }

      // Store cleanup function
      cleanup = () => {
        elements.forEach((element) => {
          observer.unobserve(element);
        });
        if (handleScroll) {
          window.removeEventListener('scroll', handleScroll);
        }
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        if (observerTimeout) {
          clearTimeout(observerTimeout);
          observerTimeout = null;
        }
        pendingActiveId = null;
      };
    });

    return () => {
      // Cleanup observer if it was set up
      if (cleanup) {
        cleanup();
      }
    };
  }, [headings, headerOffset, calculatedRootMargin, threshold, findMostVisibleHeading]);

  return activeId;
}
